/**
 * Claude-Code-style clarifying-question card.
 *
 * Options render as selectable chips. If `allowCustom` is true, an "Other"
 * chip reveals a free-text box.
 *
 * Only ever mounted for an unanswered question — ``MessageBubble`` renders a
 * resolved question as plain prose rather than mounting this card.
 */

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Chip, IconButton, Paper, Stack, TextField, Tooltip } from '@mui/material';
import { useState } from 'react';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import { Markdown } from './Markdown';
import type { EpiClarifyingQuestion } from '../types/epi-chat-types';

interface ClarifyingQuestionCardProps {
  question: EpiClarifyingQuestion;
  onAnswer: (answer: { text: string; questionId: string; choice?: string }) => void;
  disabled?: boolean;
}

export const ClarifyingQuestionCard = ({
  question,
  onAnswer,
  disabled,
}: ClarifyingQuestionCardProps) => {
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  // Drill-in: the candidate concept group the researcher is previewing before
  // choosing. Lets them compare contents without leaving the conversation.
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);

  const handleOption = (label: string) => {
    onAnswer({ text: label, questionId: question.questionId, choice: label });
  };

  const handleCustomSubmit = () => {
    const trimmed = customText.trim();
    if (!trimmed) return;
    onAnswer({ text: trimmed, questionId: question.questionId });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        padding: 1.5,
        borderColor: (theme) => theme.palette.primary.main,
        borderRadius: 1,
      }}
    >
      <Box sx={{ marginBottom: 0.75 }}>
        <Markdown dense>{question.question}</Markdown>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        {question.options.map((opt) => (
          <Stack
            key={opt.label}
            direction="row"
            alignItems="center"
            sx={{ marginBottom: 0.5, maxWidth: '100%', minWidth: 0 }}
          >
            <Chip
              label={opt.label}
              onClick={() => handleOption(opt.label)}
              disabled={disabled}
              variant="outlined"
              title={opt.description}
              sx={{
                height: 'auto',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  paddingTop: 0.5,
                  paddingBottom: 0.5,
                },
              }}
            />
            {opt.conceptGroupId && (
              <Tooltip title="View this concept group">
                <IconButton
                  size="small"
                  aria-label={`View concept group ${opt.label}`}
                  onClick={() => setOpenConceptGroupId(opt.conceptGroupId ?? null)}
                  sx={{ marginLeft: 0.25 }}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
        {question.allowCustom && !customMode && (
          <Chip
            label="Other…"
            onClick={() => setCustomMode(true)}
            disabled={disabled}
            variant="outlined"
            sx={{ marginBottom: 0.5 }}
          />
        )}
      </Stack>

      {customMode && (
        <Box sx={{ marginTop: 1.5, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Type your answer"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleCustomSubmit();
              }
            }}
            disabled={disabled}
          />
          <Button
            variant="contained"
            onClick={handleCustomSubmit}
            disabled={disabled || !customText.trim()}
            size="small"
          >
            Send
          </Button>
        </Box>
      )}

      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
    </Paper>
  );
};
